<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  
  export let placeholder: string = 'Search and select...';
  export let selectedValue: string = '';
  export let onSelectionChange: (value: string) => void = () => {};
  export let id: string | undefined = undefined;
  export let searchFunction: (query: string) => Promise<Array<{uuid: string, name: string}>>;
  export let getSelectedLabel: (uuid: string) => Promise<string | null> = async () => null;
  export let debounceMs: number = 300;
  
  let isOpen = false;
  let searchInput = '';
  let options: Array<{uuid: string, name: string}> = [];
  let isLoading = false;
  let searchTimeout: ReturnType<typeof setTimeout> | null = null;
  let dropdownRef: HTMLDivElement;
  let selectedLabel: string = '';


  // Load selected label when component mounts or selectedValue changes
  $: if (selectedValue) {
    loadSelectedLabel();
  }

  async function loadSelectedLabel() {
    if (!selectedValue) {
      selectedLabel = '';
      searchInput = '';
      return;
    }
    
    try {
      const label = await getSelectedLabel(selectedValue);
      if (label) {
        selectedLabel = label;
        searchInput = label;
      }
    } catch (err) {
      console.error('Error loading selected label:', err);
    }
  }

  function handleInputFocus() {
    isOpen = true;
    if (!searchInput && selectedLabel) {
      searchInput = selectedLabel;
    }
    // Don't load options on focus - only when user types
    // This prevents loading all results initially
  }

  function handleInputChange() {
    const query = searchInput.trim();
    isOpen = true;
    
    // Clear existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    // If input is cleared, reset selection and clear options
    if (!query) {
      selectedValue = '';
      selectedLabel = '';
      onSelectionChange('');
      options = [];
      isOpen = false; // Close dropdown when input is cleared
      return;
    }
    
    // Only search if query has at least 2 characters (prevents too many results)
    if (query.length >= 2) {
      // Debounce the search
      searchTimeout = setTimeout(() => {
        performSearch(query);
      }, debounceMs);
    } else {
      // Clear options if query is too short
      options = [];
    }
  }

  async function performSearch(query: string) {
    if (!searchFunction) return;
    
    try {
      isLoading = true;
      const results = await searchFunction(query);
      
      // Create new array reference to trigger Svelte reactivity
      options = (results && Array.isArray(results)) ? [...results] : [];
    } catch (err) {
      console.error('Error in search:', err);
      options = [];
    } finally {
      isLoading = false;
    }
  }

  function handleSelect(option: {uuid: string, name: string}) {
    selectedValue = option.uuid;
    selectedLabel = option.name;
    searchInput = option.name;
    isOpen = false;
    onSelectionChange(option.uuid);
  }

  function handleClickOutside(event: MouseEvent) {
    if (dropdownRef && !dropdownRef.contains(event.target as Node)) {
      isOpen = false;
      // Restore the selected value display
      if (selectedLabel) {
        searchInput = selectedLabel;
      } else {
        searchInput = '';
      }
    }
  }

  onMount(() => {
    document.addEventListener('mousedown', handleClickOutside);
    if (selectedValue) {
      loadSelectedLabel();
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  onDestroy(() => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
  });
</script>

<div class="async-dropdown-container" bind:this={dropdownRef}>
  <div class="input-wrapper">
    <input
      {id}
      type="text"
      class="dropdown-input"
      placeholder={placeholder}
      bind:value={searchInput}
      on:focus={handleInputFocus}
      on:input={handleInputChange}
    />
    <div class="dropdown-icon" class:open={isOpen}>
      {#if isLoading}
        <div class="loading-spinner"></div>
      {:else}
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
          <path d="M7 10l5 5 5-5z"/>
        </svg>
      {/if}
    </div>
  </div>
  
  {#if isOpen}
    <div class="options-container">
      {#if isLoading}
        <div class="loading-state">Searching...</div>
      {:else if !options || options.length === 0}
        <div class="no-results">
          {searchInput.trim().length >= 2 ? 'No results found' : searchInput.trim().length > 0 ? 'Type at least 2 characters...' : 'Start typing to search...'}
        </div>
      {:else}
        <div class="debug-info" style="display: none;">Options count: {options.length}</div>
        {#each options as option (option.uuid)}
          <div
            class="option"
            class:selected={option.uuid === selectedValue}
            on:click={() => handleSelect(option)}
            role="button"
            tabindex="0"
            on:keydown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelect(option);
              }
            }}
          >
            {option.name}
          </div>
        {/each}
      {/if}
    </div>
  {/if}
</div>

<style>
  .async-dropdown-container {
    position: relative;
    display: inline-block;
    width: 100%;
    min-width: 200px;
  }
  
  .input-wrapper {
    position: relative;
    width: 100%;
  }
  
  .dropdown-input {
    width: 100%;
    appearance: none;
    background-color: white;
    border: 1px solid #e2e8f0;
    border-radius: 50rem !important;
    padding: 0.75rem 2.5rem 0.75rem 1rem;
    font-size: 0.875rem;
    color: #4a5568;
    cursor: text;
    font-weight: 500;
    box-sizing: border-box;
  }
  
  .dropdown-input:focus {
    outline: none;
    border-color: #f0b429;
    box-shadow: 0 0 0 3px rgba(240, 180, 41, 0.1);
  }
  
  .dropdown-input:hover {
    border-color: #cbd5e0;
  }
  
  .dropdown-icon {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #6b7280;
    transition: transform 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .dropdown-icon.open {
    transform: translateY(-50%) rotate(180deg);
  }

  .loading-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid #e2e8f0;
    border-top-color: #6b7280;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
  
  .options-container {
    position: absolute;
    top: calc(100% + 0.25rem);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    max-height: 300px;
    overflow-y: auto;
    z-index: 50;
  }
  
  .option {
    padding: 0.75rem 1rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: #4a5568;
    transition: background-color 0.2s;
  }
  
  .option:hover {
    background-color: #f7fafc;
  }
  
  .option.selected {
    background-color: #f0b42911;
    color: #f0b429;
    font-weight: 500;
  }
  
  .option:focus {
    outline: none;
    background-color: #f7fafc;
  }
  
  .no-results,
  .loading-state {
    padding: 0.75rem 1rem;
    color: #a0aec0;
    font-size: 0.875rem;
    text-align: center;
  }

  .loading-state {
    color: #6b7280;
  }
  
  /* Scrollbar Styling */
  .options-container::-webkit-scrollbar {
    width: 6px;
  }
  
  .options-container::-webkit-scrollbar-track {
    background: #f7fafc;
    border-radius: 3px;
  }
  
  .options-container::-webkit-scrollbar-thumb {
    background: #e2e8f0;
    border-radius: 3px;
  }
  
  .options-container::-webkit-scrollbar-thumb:hover {
    background: #cbd5e0;
  }
</style>

